"use client";
import Link from "next/link";
import React, { useCallback } from "react";
import toast from "react-hot-toast";

interface Props {
  country: string;
  status: string;
  number: string;
  href: string;
  isPrivate?: boolean;
  price?: number;
  guaranteeText?: string;
}

export default function NumberCard({
  country,
  status,
  number,
  href,
  isPrivate = false,
  price,
  guaranteeText,
}: Props) {
  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(number);
    toast.success("Number Copied");
  }, [number]);
  return (
      <div
        className={
          isPrivate
            ? `bg-white flex items-center justify-between space-y-6 flex-col w-full min-h-[280px] p-6 ${
                status === "available" ? " border-[#090EDB]" : "border-gray-300"
              } border-[2px] rounded-xl`
            : `bg-white flex items-center justify-between space-y-6 flex-col w-full min-h-[280px] p-6 border ${
                status === "online" ? "border-[#48C78E]" : "border-gray-300"
              }  rounded-xl`
        }
      >
        <div className=" w-full flex items-center justify-evenly">
          <p className="text-xl font-bold font-spaceGrotesk text-zinc-700">
            {country}
          </p>

          {isPrivate ? (
            <span
              className={` ${
                status === "available"
                  ? "bg-[#E73DC4]/20 text-[#090EDB]"
                  : "bg-gray-400/20 text-gray-500"
              } capitalize font-medium text-sm py-2 px-4 rounded-full `}
            >
              {status}
            </span>
          ) : (
            <span
              className={` ${
                status === "online"
                  ? "bg-[#16D032]/20 text-[#1F1F1F]"
                  : "bg-gray-400/20 text-gray-500"
              } capitalize font-medium text-sm py-2 px-4 rounded-full `}
            >
              {status}
            </span>
          )}
        </div>

        <div className="flex items-center justify-evenly w-full">
          <div
            className={`w-2.5 h-2.5 rounded-full ${
              isPrivate
                ? status === "available"
                  ? "bg-gradient-to-tr from-[#090EDB] to-[#E73DC4]"
                  : "bg-gray-500"
                : status === "online"
                  ? "bg-sb_green"
                  : "bg-gray-500"
            }`}
          />

          <p className="font-spaceGrotesk text-2xl md:text-xl lg:text-lg font-semibold">
            {isPrivate ? number.slice(0, 10) + "****" : number}
          </p>
          {/* Private numbers must stay hidden until rented — no copy control. */}
          {!isPrivate && (
          <div
            onClick={copyToClipboard}
            className="p-2 hover:bg-gray-100 cursor-pointer rounded-full"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="h-6 w-6 md:w-5 md:h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75"
              />
            </svg>
          </div>
          )}
        </div>

        <div className="w-full flex items-center justify-center flex-col">
          {guaranteeText && (
            <p className="inline-flex items-center gap-1 text-xs font-semibold text-sb_green mb-2 text-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-4 h-4 shrink-0"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z"
                />
              </svg>
              {guaranteeText}
            </p>
          )}
          {/* Weekly price intentionally not shown on cards (conversion). */}
          {isPrivate ? (
            <Link href={status === "reserved" ? "#" : href}>
              <div
                className={`${
                  status === "available"
                    ? "bg-gradient-to-tr from-[#090EDB] to-[#E73DC4] hover:bg-gradient-to-tr hover:from-[#090EDB]/80 hover:to-[#E73DC4]/80 "
                    : "bg-gray-300"
                } px-10 transition-all duration-300 hover:px-14 py-3 text-white font-semibold rounded-lg`}
              >
                {status === "available" ? "Rent Number" : "Reserved"}
              </div>
            </Link>
          ) : (
            <Link href={status === "offline" ? "#" : href}>
              <div
                className={`px-10 transition-all duration-300 hover:px-14 py-3 ${
                  status === "online"
                    ? "bg-[#28622E] hover:bg-sb_green/90"
                    : "bg-gray-300"
                }  text-white font-semibold rounded-lg`}
              >
                {status === "online" ? "Use Number" : "Offline"}
              </div>
            </Link>
          )}
        </div>
      </div>
  );
}
